import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Pencil, Plus, Search, Trash2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ViewModeToggle } from '@/components/ui/view-mode-toggle';
import { PermissionDenied } from '@/components/ui/permission-denied';
import type { ApiError } from '@/core/api';
import { createCabinetViewStore, viewModes } from '@/features/cabinet/store/cabinetView';
import { RenderTemplateDialog } from '../components/RenderTemplateDialog';
import { TemplateFormDialog } from '../components/TemplateFormDialog';
import { TEMPLATE_TYPES, TEMPLATE_TYPE_LABEL } from '../constants';
import {
  useCreateTemplate,
  useDeleteTemplate,
  useTemplates,
  useUpdateTemplate,
} from '../queries';
import type { Template, TemplateInput } from '../types';

const MODES = viewModes(['table', 'list', 'cards', 'grouped']);
const useTemplatesUi = createCabinetViewStore('documents.templates:ui', 'table');

const ActiveBadge = ({ active }: { active: boolean }) => (
  <Badge variant={active ? 'secondary' : 'outline'}>{active ? 'Active' : 'Inactive'}</Badge>
);

export function TemplatesListPage() {
  const navigate = useNavigate();
  const view = useTemplatesUi((s) => s.view);
  const setView = useTemplatesUi((s) => s.setView);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Template | undefined>(undefined);
  const [rendering, setRendering] = useState<Template | null>(null);
  const [toDelete, setToDelete] = useState<Template | null>(null);

  const { data, isPending, isError, error, isFetching } = useTemplates({
    search: search || undefined,
  });
  const create = useCreateTemplate();
  const update = useUpdateTemplate();
  const remove = useDeleteTemplate();

  const templates = data?.results ?? [];

  function applySearch(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput.trim());
  }

  async function handleSubmit(input: TemplateInput) {
    if (editing) await update.mutateAsync({ id: editing.id, input });
    else await create.mutateAsync(input);
  }

  const actions = (t: Template) => (
    <>
      <Button size="sm" variant="ghost" onClick={() => setRendering(t)}>
        <FileText className="size-4" /> Render
      </Button>
      <Button
        size="icon"
        variant="ghost"
        aria-label="Edit template"
        onClick={() => {
          setEditing(t);
          setFormOpen(true);
        }}
      >
        <Pencil className="size-4" />
      </Button>
      <Button size="icon" variant="ghost" aria-label="Delete template" onClick={() => setToDelete(t)}>
        <Trash2 className="size-4" />
      </Button>
    </>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Template Library</h1>
        <Button
          onClick={() => {
            setEditing(undefined);
            setFormOpen(true);
          }}
        >
          <Plus className="size-4" /> New template
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={applySearch} className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground absolute top-2.5 left-2.5 size-4" />
          <Input
            className="pl-8"
            placeholder="Search templates"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </form>
        <ViewModeToggle value={view} onChange={setView} modes={MODES} className="ml-auto" />
      </div>

      {isError ? (
        (error as unknown as ApiError)?.status === 403 ? (
          <PermissionDenied message={(error as unknown as ApiError)?.message} />
        ) : (
          <p className="text-destructive text-sm">
            {(error as unknown as ApiError)?.message ?? 'Failed to load templates.'}
          </p>
        )
      ) : isPending ? (
        <Skeleton className="h-48 w-full" />
      ) : view === 'table' ? (
        <TemplatesTable rows={templates} isFetching={isFetching} renderActions={actions} />
      ) : view === 'list' ? (
        <TemplatesList rows={templates} renderActions={actions} />
      ) : view === 'cards' ? (
        <TemplatesCards rows={templates} renderActions={actions} />
      ) : (
        <TemplatesGroupedByType rows={templates} renderActions={actions} />
      )}

      <TemplateFormDialog open={formOpen} onOpenChange={setFormOpen} template={editing} onSubmit={handleSubmit} />

      <RenderTemplateDialog
        open={Boolean(rendering)}
        onOpenChange={(o) => !o && setRendering(null)}
        template={rendering}
        onRendered={() => navigate('/documents/files')}
      />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete template?</AlertDialogTitle>
            <AlertDialogDescription>
              “{toDelete?.name}” will be removed. Documents already rendered from it are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (toDelete) remove.mutate(toDelete.id);
                setToDelete(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface ViewProps {
  rows: Template[];
  renderActions: (t: Template) => React.ReactNode;
}

const EmptyText = () => <p className="text-muted-foreground text-sm">No templates yet.</p>;

function TemplatesTable({ rows, isFetching, renderActions }: ViewProps & { isFetching: boolean }) {
  return (
    <div className="rounded-md border" aria-busy={isFetching}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-32 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground py-10 text-center">
                No templates yet.
              </TableCell>
            </TableRow>
          ) : (
            rows.map((t) => (
              <TableRow key={t.id}>
                <TableCell className="font-medium">{t.name}</TableCell>
                <TableCell>{TEMPLATE_TYPE_LABEL[t.type]}</TableCell>
                <TableCell className="text-muted-foreground">v{t.version}</TableCell>
                <TableCell>
                  <ActiveBadge active={t.is_active} />
                </TableCell>
                <TableCell className="text-right">{renderActions(t)}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function TemplatesList({ rows, renderActions }: ViewProps) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <ul className="divide-y rounded-md border">
      {rows.map((t) => (
        <li key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm">
          <span className="min-w-0 flex-1 truncate font-medium">{t.name}</span>
          <span className="text-muted-foreground hidden sm:inline">{TEMPLATE_TYPE_LABEL[t.type]}</span>
          <span className="text-muted-foreground hidden sm:inline">v{t.version}</span>
          <ActiveBadge active={t.is_active} />
          <span className="flex items-center">{renderActions(t)}</span>
        </li>
      ))}
    </ul>
  );
}

function TemplatesCards({ rows, renderActions }: ViewProps) {
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {rows.map((t) => (
        <Card key={t.id}>
          <CardHeader>
            <CardTitle className="flex items-center justify-between gap-2 text-base">
              <span className="min-w-0 truncate">{t.name}</span>
              <ActiveBadge active={t.is_active} />
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground flex items-center justify-between gap-2 text-xs">
            <span>
              {TEMPLATE_TYPE_LABEL[t.type]} · v{t.version}
            </span>
            <span className="flex items-center">{renderActions(t)}</span>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function TemplatesGroupedByType({ rows, renderActions }: ViewProps) {
  const groups = useMemo(
    () =>
      TEMPLATE_TYPES.map((t) => ({
        key: t.value,
        label: t.label,
        bucket: rows.filter((r) => r.type === t.value),
      })).filter((g) => g.bucket.length > 0),
    [rows],
  );
  if (rows.length === 0) return <EmptyText />;
  return (
    <div className="space-y-6">
      {groups.map(({ key, label, bucket }) => (
        <section key={key}>
          <h2 className="text-muted-foreground mb-2 flex items-center gap-2 text-xs font-semibold tracking-wider uppercase">
            {label}
            <Badge variant="outline" className="text-[10px]">
              {bucket.length}
            </Badge>
          </h2>
          <TemplatesCards rows={bucket} renderActions={renderActions} />
        </section>
      ))}
    </div>
  );
}
